/**
 * Hardware combination rules. Independent of SVG placement metadata.
 *
 * The slab SVG (`#hardware-guides`) tells JARVIS where hardware may go.
 * This module tells JARVIS which combinations are logically valid.
 * Do not infer compatibility from geometry, and do not encode these rules
 * in slab artwork.
 */

import type { DoorHardwareConfiguration } from '../types/doorConfiguration';

export const PULL_BAR_LENGTHS_MM = [400, 900, 1200, 1800] as const;
export type PullBarLengthMm = (typeof PULL_BAR_LENGTHS_MM)[number];

export type HardwareFace = 'external' | 'internal';

export type HardwareRoleId =
  | 'lever-handle'
  | 'pull-bar'
  | 'finger-pull-escutcheon'
  | 'escutcheon'
  | 'door-knob'
  | 'rim-cylinder'
  | 'euro-rim'
  | 'lever-on-rose'
  | 'letterplate'
  | 'knocker'
  | 'spyhole';

export type HardwarePosition = 'standard' | 'heritage';

export interface FaceHardwareIntent {
  roles: HardwareRoleId[];
  position?: HardwarePosition | null;
  pullBarLengthMm?: PullBarLengthMm | null;
}

export interface HardwareCompatibilityResult {
  valid: boolean;
  reasons: string[];
}

const EXTERNAL_OPERATING: HardwareRoleId[] = [
  'lever-handle',
  'pull-bar',
  'finger-pull-escutcheon',
];

function hasRole(roles: HardwareRoleId[], role: HardwareRoleId): boolean {
  return roles.includes(role);
}

function operatingRoles(roles: HardwareRoleId[]): HardwareRoleId[] {
  return roles.filter((role) => EXTERNAL_OPERATING.includes(role));
}

export function isValidPullBarLength(lengthMm: number): lengthMm is PullBarLengthMm {
  return (PULL_BAR_LENGTHS_MM as readonly number[]).includes(lengthMm);
}

export function isExternalDoorKnobAllowed(): false {
  return false;
}

export function canCombinePullBarWithExternalLever(): false {
  return false;
}

export function canCombinePullBarWithRimCylinder(): true {
  return true;
}

export function fingerPullAllowsSeparateExternalHandle(): false {
  return false;
}

export function pullBarRequiresEscutcheon(): true {
  return true;
}

export function escutcheonRequiresPullBar(): true {
  return true;
}

export function canCombineEscutcheonWithLever(): false {
  return false;
}

export function allowsAsymmetricInternalExternalHardware(): true {
  return true;
}

export function canCombineKnockerWithSpyhole(): false {
  return false;
}

export function evaluateFaceHardware(
  face: HardwareFace,
  intent: FaceHardwareIntent
): HardwareCompatibilityResult {
  const reasons: string[] = [];
  const { roles } = intent;

  if (face === 'external') {
    if (hasRole(roles, 'door-knob')) {
      reasons.push('A door knob is not offered as an external operating handle.');
    }

    if (hasRole(roles, 'pull-bar') && hasRole(roles, 'lever-handle')) {
      reasons.push('A pull bar must not be combined with an external lever handle.');
    }

    if (hasRole(roles, 'escutcheon') && hasRole(roles, 'lever-handle')) {
      reasons.push('An escutcheon must be combined with a bar handle, not a lever.');
    }

    if (hasRole(roles, 'pull-bar') && !hasRole(roles, 'escutcheon')) {
      reasons.push('A bar handle must be combined with an escutcheon.');
    }

    if (hasRole(roles, 'escutcheon') && !hasRole(roles, 'pull-bar')) {
      reasons.push('An escutcheon must be combined with a bar handle.');
    }

    if (
      hasRole(roles, 'finger-pull-escutcheon') &&
      (hasRole(roles, 'pull-bar') || hasRole(roles, 'lever-handle'))
    ) {
      reasons.push(
        'A finger-pull escutcheon must not be combined with a pull bar or external lever handle.'
      );
    }

    if (operatingRoles(roles).length > 1) {
      reasons.push('Only one external operating handle may be selected.');
    }
  }

  if (hasRole(roles, 'knocker') && hasRole(roles, 'spyhole')) {
    reasons.push('A knocker and a spyhole cannot be selected together.');
  }

  if (
    hasRole(roles, 'pull-bar') &&
    intent.pullBarLengthMm != null &&
    !isValidPullBarLength(intent.pullBarLengthMm)
  ) {
    reasons.push('The selected pull-bar length is not supported.');
  }

  return { valid: reasons.length === 0, reasons };
}

/**
 * Cross-face check. Internal and external furniture may differ, including
 * heritage finger-pull outside with a lever-on-rose inside at heritage.
 */
export function evaluateHardwareCompatibility(input: {
  external: FaceHardwareIntent;
  internal: FaceHardwareIntent;
}): HardwareCompatibilityResult {
  const external = evaluateFaceHardware('external', input.external);
  const internal = evaluateFaceHardware('internal', input.internal);
  const reasons = [...external.reasons, ...internal.reasons];
  const combinedRoles = [...input.external.roles, ...input.internal.roles];
  if (hasRole(combinedRoles, 'knocker') && hasRole(combinedRoles, 'spyhole')) {
    reasons.push('A knocker and a spyhole cannot be selected together.');
  }

  return { valid: reasons.length === 0, reasons: [...new Set(reasons)] };
}

const ACCESSORY_ROLES: HardwareRoleId[] = ['knocker', 'spyhole'];

export function exclusiveAccessorySelection(
  roles: HardwareRoleId[],
  accessory: 'knocker' | 'spyhole' | null
): HardwareRoleId[] {
  const withoutAccessories = roles.filter((role) => !ACCESSORY_ROLES.includes(role));
  if (!accessory) return withoutAccessories;
  return [...withoutAccessories, accessory];
}

export function withExclusiveAccessory(
  hardware: DoorHardwareConfiguration,
  face: HardwareFace,
  accessory: 'knocker' | 'spyhole' | null
): DoorHardwareConfiguration {
  const kept = hardware[face].filter(
    (item) => item.id !== 'knocker' && item.id !== 'spyhole'
  );
  return {
    ...hardware,
    [face]: accessory
      ? [...kept, { id: accessory, placementId: `${accessory}-anchor` }]
      : kept,
  };
}

export function withoutKnockerAndSpyhole(
  hardware: DoorHardwareConfiguration
): DoorHardwareConfiguration {
  return {
    ...hardware,
    external: hardware.external.filter(
      (item) => item.id !== 'knocker' && item.id !== 'spyhole'
    ),
    internal: hardware.internal.filter(
      (item) => item.id !== 'knocker' && item.id !== 'spyhole'
    ),
  };
}
